// The path INTO the page that an inline edit in the preview is about.
//
// The page editor's half of what `sectionPath` is for the section editor
// (routes/sites/site/sections/section/edit/preview.js), and it exists for the
// same stated reason: the frame is a MESSAGE BOUNDARY. What arrives at
// `onInput` is a `datapath` and a `value` from a postMessage, and what the
// editor does with the pair is `_set(page, datapath, value)` — lodash's set,
// which creates every key on the way down. Unvalidated, that is an arbitrary
// write into the object the Save button posts.
//
// P173, and the row's second half is the one that happens without an attacker.
// `editorMarkup` in @common/render/template rewrites `data-edit="heading"` to
// `data-edit="elements[N].config.values.heading"`, and ./elements.js passes the
// editor context for `code` elements ONLY — a section renders with `null`, so a
// section's own `data-edit` attributes keep their bare, section-relative form.
// preview/mount.js then wires EVERY `[data-edit]` node as contentEditable, so
// typing into text that came from a section used to send `heading` and write a
// stray top-level `heading` key onto the page. docs/pagews.md has always said
// inline editing is "offered for code elements only"; this and the shape check
// in preview/mount.js are what enforce it.
//
// THE ELEMENT IS CHECKED AGAINST THE PAGE, not just against the shape. An index
// the page does not have, or one that is not a `code` element, is refused —
// which is what makes this the enforcement rather than a restatement of the
// composer's format.
//
// THE TAIL IS TENANT-AUTHORED. Everything after `values.` is the string the
// element's own html put in its `data-edit` attribute, so `__proto__.polluted`
// is a value a site's author can type, and the tail is matched against the
// shape a value path can have rather than trusted.
//
// The three names refused below are defence in depth, and are labelled as such
// rather than as a fix: lodash 4.17.21's `baseSet` already refuses `__proto__`,
// `constructor` and `prototype` as keys, so today such a path writes nothing —
// which is its own small lie, since the editor would then treat a no-op as an
// edit. They are named here so the answer does not rest on a dependency's
// internals, and because a path shape that admits them is not one anybody
// meant to allow.
//
// The RECONSTRUCTED path is what is returned, never the caller's string: what
// reaches `_set` is then built out of an integer and a tail that matched, and
// no part of the message survives unexamined.
const ELEMENT_PATH = /^elements\[(\d+)\]\.config\.values\.(.+)$/;

// `a`, `a.b`, `a[0].b` — dot segments and numeric indices, and nothing else. In
// particular no bracketed string keys, which are the other way to write a name
// this file refuses by name below.
const VALUE_PATH = /^[A-Za-z_][A-Za-z0-9_-]*(?:\[\d+\]|\.[A-Za-z_][A-Za-z0-9_-]*)*$/;

const REFUSED_KEYS = new Set(["__proto__", "constructor", "prototype"]);

export const editablePath = (datapath, elements) => {
  const matched = ELEMENT_PATH.exec(datapath || "");

  if (!matched) {
    return null;
  }

  const index = Number(matched[1]);
  const element = (elements || [])[index];

  // A `section` element is the case this is really about; anything that is not
  // `code` is refused for the same reason, which is that the composer never
  // offered it an absolute path and so never meant it to be edited here.
  if (!element || element.type !== "code") {
    return null;
  }

  const tail = matched[2];

  if (!VALUE_PATH.test(tail)) {
    return null;
  }

  if (tail.split(/[.[\]]+/).some((segment) => REFUSED_KEYS.has(segment))) {
    return null;
  }

  return `elements[${index}].config.values.${tail}`;
};
